import { FC, useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Field, PlatformCard, StatusChip } from './PlatformCard';
import { ActivateDialog, EndDialog, ExtendDialog, LinkCompanyDialog, NoticeDialog, SeatsDialog } from './PlatformAccountDialogs';
import PlanFormDialog from './PlanFormDialog';
import { usePageTitle } from './PlatformHeader';
import { IPlatformAdmin } from './PlatformLayout';
import { EVENT_LABEL, errorMessage, formatDate, formatDateTime, formatLimit, planLabel, platformApi } from './platformApi';

type Product = 'erp' | 'pos';
type DialogState = { kind: 'extend' | 'reopen' | 'end' | 'activate' | 'seats' | 'custom' | 'link' | 'unlink' | 'notice'; product: Product; planCode?: string; company?: { _id: string; name: string } } | null;

const USAGE_LABEL: Record<string, string> = {
  users: 'Users',
  locations: 'Locations',
  items: 'Items',
  employees: 'Employees',
  invoicesPerMonth: 'Invoices this month',
  storageMB: 'File storage (MB)',
  ocrPerMonth: 'OCR scans this month',
};

const SOURCE_LABEL: Record<string, string> = {
  signup: 'Signup trial',
  legacy: 'Legacy migration',
  manual: 'Manual payment',
  gateway: 'Payment gateway',
  comp: 'Complimentary',
  contract: 'Contract',
};

const actionButton = (label: string, onClick: () => void, color: 'primary' | 'error' = 'primary') => (
  <Button key={label} size='small' variant='outlined' color={color} onClick={onClick} sx={{ textTransform: 'none', height: 28, bgcolor: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>{label}</Button>
);

// ended = expired, or past the grace period even if the daily job has not run yet
const isEnded = (sub: any, status?: string) =>
  (status || sub?.status) === 'expired' || (!!sub?.graceEndsAt && new Date(sub.graceEndsAt) < new Date());

const PlatformAccountDetail: FC = () => {
  const { accountId = '' } = useParams();
  const [data, setData] = useState<any>();
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState<DialogState>(null);
  const [basePlans, setBasePlans] = useState<any[]>([]);
  const isOwner = useOutletContext<IPlatformAdmin | undefined>()?.role === 'owner';

  const load = useCallback(() => {
    platformApi.get(`/accounts/${accountId}`)
      .then((response) => { setData(response.data); setError(''); })
      .catch((err) => setError(errorMessage(err)));
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  // custom plans start from a standard plan
  useEffect(() => {
    if (isOwner) platformApi.get('/plans').then((response) => setBasePlans(response.data.plans.filter((p: any) => !p.forAccount))).catch(() => undefined);
  }, [isOwner]);

  const close = () => setDialog(null);
  // POS actions act on the chosen company's own POS subscription
  const target = dialog?.company?._id || accountId;
  const targetPos = data?.posByCompany?.find((row: any) => row.company._id === target)?.pos;
  const account = data?.account;
  usePageTitle(account?.name);

  // the owner holds the ERP plan; POS is per company, an add-on that needs the ERP plan
  const posNeedsErp = (product: Product) => product === 'pos' && !data?.erp;
  const subscriptionCard = (product: Product, sub: any, company?: { _id: string; name: string }) => (
    <PlatformCard
      title={product === 'erp' ? "Owner's ERP subscription" : `POS add-on · ${company?.name}`}
      fill
      footer={posNeedsErp(product) ? undefined :
        <>
          {sub && isEnded(sub, product === 'erp' ? data.effectiveStatus : undefined) && actionButton('Reopen', () => setDialog({ kind: 'reopen', product, company }))}
          {sub && !isEnded(sub, product === 'erp' ? data.effectiveStatus : undefined) && actionButton('Extend', () => setDialog({ kind: 'extend', product, company }))}
          {sub && product === 'erp' && actionButton('Seats', () => setDialog({ kind: 'seats', product }))}
          {isOwner && actionButton('Custom plan', () => setDialog({ kind: 'custom', product, company }))}
          {actionButton(sub ? 'Change plan' : 'Activate', () => setDialog({ kind: 'activate', product, company }))}
          {sub && actionButton('Send notice', () => setDialog({ kind: 'notice', product, company }))}
          {sub && !isEnded(sub, product === 'erp' ? data.effectiveStatus : undefined) && actionButton('End now', () => setDialog({ kind: 'end', product, company }), 'error')}
        </>
      }
    >
      {!sub && <h5 style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 14, margin: 0 }}>{product === 'erp' ? 'No subscription record yet. Run the migration or activate a plan.' : posNeedsErp(product) ? 'POS is an add-on to the ERP plan. Activate an ERP plan first.' : 'POS is not subscribed.'}</h5>}
      {sub && (
        <Grid container spacing={2}>
          <Grid item xs={6} md={4}><Field title='Plan'>{sub.planName || planLabel(sub.planCode)}</Field></Grid>
          <Grid item xs={6} md={4}><Field title='Status'><StatusChip status={product === 'erp' ? data.effectiveStatus || sub.status : sub.status} /></Field></Grid>
          <Grid item xs={6} md={4}><Field title='Source'>{SOURCE_LABEL[sub.source] || sub.source}</Field></Grid>
          <Grid item xs={6} md={4}><Field title='Started'>{formatDate(sub.startDate)}</Field></Grid>
          <Grid item xs={6} md={4}><Field title={sub.status === 'trialing' ? 'Trial ends' : 'Valid till'}>{formatDate(sub.status === 'trialing' ? sub.trialEndsAt : sub.endDate)}</Field></Grid>
          <Grid item xs={6} md={4}><Field title='Grace ends'>{formatDate(sub.graceEndsAt)}</Field></Grid>
          {product === 'erp' && <Grid item xs={6} md={4}><Field title='Extra seats'>{sub.extraSeats?.users || 0} users, {sub.extraSeats?.locations || 0} locations</Field></Grid>}
          {product === 'erp' && <Grid item xs={6} md={4}><Field title='Legacy full access'>{sub.hasOverrides ? 'Yes' : 'No'}</Field></Grid>}
          <Grid item xs={6} md={4}><Field title='Payment reference'>{sub.paymentRef || '-'}</Field></Grid>
          {product === 'pos' && (
            <Grid item xs={12}>
              <Field title={`Licensed outlets (${(sub.posLocations || []).length})`}>
                {(sub.posLocations || []).map((id: string) => {
                  const l = data.locations.find((loc: any) => String(loc._id) === String(id));
                  return <div key={id}>{l ? `${l.name} (${l.code})` : 'Outlet not found'}</div>;
                })}
                {!(sub.posLocations || []).length && '-'}
              </Field>
            </Grid>
          )}
        </Grid>
      )}
    </PlatformCard>
  );

  return (
    <>
      <h5 style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 13, margin: '0 0 16px' }}>
        {[account?.legalName, account?.tinGst, account?.owner && `Owner: ${account.owner.name} (${account.owner.email}) holds the plan for all their companies`].filter(Boolean).join(' · ')}
      </h5>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>{subscriptionCard('erp', data.erp)}</Grid>
          {/* POS is per company */}
          {data.posByCompany.map((row: any) => (
            <Grid item xs={12} md={6} key={row.company._id}>{subscriptionCard('pos', row.pos, row.company)}</Grid>
          ))}

          {data.erp && (
            <Grid item xs={12}>
              <PlatformCard
                title={`Companies on the owner's plan (${data.companies.covered.length}${data.companies.limit >= 0 ? ` / ${data.companies.limit}` : ''})`}
                action={data.companies.linkable.length ? actionButton('Link company', () => setDialog({ kind: 'link', product: 'erp' })) : undefined}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data.companies.covered.map((c: any) => (
                    <Chip
                      key={c._id}
                      variant='outlined'
                      label={`${c.name}${c._id === data.companies.payingAccount ? ' · paying' : ''}`}
                    />
                  ))}
                  {data.companies.linkable.map((c: any) => <Chip key={c._id} color='warning' variant='outlined' label={`${c.name} · own plan`} />)}
                </Box>
              </PlatformCard>
            </Grid>
          )}

          <Grid item xs={12}>
            <PlatformCard title='Usage (all companies on the plan)'>
              <Grid container spacing={2}>
                {Object.keys(USAGE_LABEL).map((key) => (
                  <Grid item xs={6} md={2} key={key}>
                    <Field title={USAGE_LABEL[key]}>
                      {Number(data.usage[key]?.used || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })} / {formatLimit(data.usage[key]?.limit)}
                    </Field>
                  </Grid>
                ))}
              </Grid>
            </PlatformCard>
          </Grid>

          <Grid item xs={12}>
            <PlatformCard title='Subscription history'>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Valid till</TableCell>
                    <TableCell>By</TableCell>
                    <TableCell>Reason</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.events.map((event: any) => (
                    <TableRow key={event._id}>
                      <TableCell>{formatDateTime(event.at)}</TableCell>
                      <TableCell>{EVENT_LABEL[event.type] || event.type}{event.product === 'pos' ? ` (POS${event.accountName ? ` · ${event.accountName}` : ''})` : ''}</TableCell>
                      <TableCell>{planLabel(event.after?.planCode)}</TableCell>
                      <TableCell>{formatDate(event.after?.endDate || event.after?.trialEndsAt)}</TableCell>
                      <TableCell>{event.actorName || 'System'}</TableCell>
                      <TableCell>{event.reason || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {!data.events.length && <TableRow><TableCell colSpan={6}>No history yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </PlatformCard>
          </Grid>
        </Grid>
      )}

      {(dialog?.kind === 'extend' || dialog?.kind === 'reopen') && <ExtendDialog accountId={target} product={dialog.product} reopen={dialog.kind === 'reopen'} onClose={close} onDone={load} />}
      {dialog?.kind === 'end' && <EndDialog accountId={target} product={dialog.product} onClose={close} onDone={load} />}
      {dialog?.kind === 'notice' && <NoticeDialog accountId={target} product={dialog.product} onClose={close} onDone={load} />}
      {dialog?.kind === 'link' && <LinkCompanyDialog accountId={accountId} product='erp' linkable={data.companies.linkable} onClose={close} onDone={load} />}
      {dialog?.kind === 'seats' && (
        <SeatsDialog accountId={accountId} product='erp' users={data.erp?.extraSeats?.users || 0} locations={data.erp?.extraSeats?.locations || 0} seatPricing={data.erp?.seatPricing} billingCycle={data.erp?.billingCycle} onClose={close} onDone={load} />
      )}
      {dialog?.kind === 'custom' && (
        <PlanFormDialog
          basePlans={basePlans}
          product={dialog.product}
          forAccount={dialog.company || { _id: accountId, name: account?.name }}
          onClose={close}
          onSaved={(plan) => setDialog({ kind: 'activate', product: dialog.product, planCode: plan.code, company: dialog.company })}
        />
      )}
      {dialog?.kind === 'activate' && (
        <ActivateDialog
          accountId={target}
          product={dialog.product}
          currentPlan={dialog.planCode || (dialog.product === 'pos' ? targetPos?.planCode : data.erp?.planCode)}
          locations={dialog.product === 'pos' ? data.locations.filter((l: any) => String(l.account) === target) : data.locations}
          licensed={(targetPos?.posLocations || []).map(String)}
          onClose={close}
          onDone={load}
        />
      )}
    </>
  );
};

export default PlatformAccountDetail;
